/**
 * Trial Risk Analysis Component
 * 5-day trial period retention analysis
 */

import { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { TrialAnalysis } from '@/lib/api/reports';
import clsx from 'clsx';

interface TrialRiskAnalysisProps {
  data: TrialAnalysis[];
  className?: string;
}

export function TrialRiskAnalysis({ data, className }: TrialRiskAnalysisProps) {
  const stats = useMemo(() => {
    const retained = data.filter((d) => d.retained);
    const churned = data.filter((d) => !d.retained);

    const avgRetainedSessions = retained.length > 0
      ? retained.reduce((sum, d) => sum + d.sessions_in_5_days, 0) / retained.length
      : 0;
    const avgChurnedSessions = churned.length > 0
      ? churned.reduce((sum, d) => sum + d.sessions_in_5_days, 0) / churned.length
      : 0;

    return {
      total: data.length,
      retained: retained.length,
      churned: churned.length,
      retentionRate: data.length > 0 ? (retained.length / data.length) * 100 : 0,
      avgRetainedSessions,
      avgChurnedSessions,
    };
  }, [data]);

  const scatterData = useMemo(() => {
    return data.map((d) => ({
      x: d.sessions_in_5_days,
      y: d.lifecycle_days,
      retained: d.retained,
    }));
  }, [data]);

  const riskGroups = useMemo(() => {
    // Group by session count in first 5 days
    const groups: Record<string, { total: number; retained: number }> = {
      '1次': { total: 0, retained: 0 },
      '2-3次': { total: 0, retained: 0 },
      '4-5次': { total: 0, retained: 0 },
      '6+次': { total: 0, retained: 0 },
    };

    data.forEach((d) => {
      let key: string;
      if (d.sessions_in_5_days <= 1) key = '1次';
      else if (d.sessions_in_5_days <= 3) key = '2-3次';
      else if (d.sessions_in_5_days <= 5) key = '4-5次';
      else key = '6+次';

      groups[key].total++;
      if (d.retained) groups[key].retained++;
    });

    return Object.entries(groups).map(([label, g]) => ({
      label,
      total: g.total,
      retained: g.retained,
      retentionRate: g.total > 0 ? (g.retained / g.total) * 100 : 0,
    }));
  }, [data]);

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="总用户数"
          value={stats.total.toString()}
          variant="default"
        />
        <StatCard
          label="留存用户"
          value={stats.retained.toString()}
          variant="profit"
        />
        <StatCard
          label="流失用户"
          value={stats.churned.toString()}
          variant="danger"
        />
        <StatCard
          label="5天留存率"
          value={`${stats.retentionRate.toFixed(1)}%`}
          variant={stats.retentionRate >= 50 ? 'profit' : 'warning'}
        />
      </div>

      {/* Risk Group Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-600 mb-3">试用期活跃度与留存关系</h4>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  5天内会话数
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  用户数
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  留存人数
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  留存率
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                  风险等级
                </th>
              </tr>
            </thead>
            <tbody>
              {riskGroups.map((group) => (
                <tr
                  key={group.label}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">{group.label}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{group.total}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{group.retained}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {group.retentionRate.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3">
                    <RiskBadge rate={group.retentionRate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scatter Plot */}
      <div>
        <h4 className="text-sm font-medium text-gray-600 mb-3">
          活跃度 vs 生命周期分布
        </h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="x"
                name="5天会话数"
                type="number"
                stroke="#888"
                fontSize={12}
                label={{
                  value: '5天会话数',
                  position: 'bottom',
                  offset: 0,
                  fill: '#666',
                  fontSize: 11,
                }}
              />
              <YAxis
                dataKey="y"
                name="生命周期(天)"
                type="number"
                stroke="#888"
                fontSize={12}
                label={{
                  value: '生命周期(天)',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#666',
                  fontSize: 11,
                }}
              />
              <Tooltip
                formatter={(value, name) => {
                  const numValue = typeof value === 'number' ? value : 0;
                  const label = name === 'x' ? '5天会话数' : '生命周期(天)';
                  return [numValue, label];
                }}
              />
              <ReferenceLine
                y={5}
                stroke="#ef4444"
                strokeDasharray="5 5"
                label={{
                  value: '5天线',
                  position: 'right',
                  fill: '#ef4444',
                  fontSize: 10,
                }}
              />
              <Scatter
                name="留存用户"
                data={scatterData.filter((d) => d.retained)}
                fill="rgba(16, 185, 129, 0.6)"
              />
              <Scatter
                name="流失用户"
                data={scatterData.filter((d) => !d.retained)}
                fill="rgba(239, 68, 68, 0.6)"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">分析洞察</h4>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>
            留存用户平均5天内会话数: <strong>{stats.avgRetainedSessions.toFixed(1)}</strong> 次
          </li>
          <li>
            流失用户平均5天内会话数: <strong>{stats.avgChurnedSessions.toFixed(1)}</strong> 次
          </li>
          {stats.avgRetainedSessions > stats.avgChurnedSessions * 1.5 && (
            <li className="text-blue-800 font-medium">
              建议: 提高试用期用户活跃度可显著提升留存率
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  variant?: 'default' | 'profit' | 'warning' | 'danger';
}

function StatCard({ label, value, variant = 'default' }: StatCardProps) {
  const variantStyles = {
    default: 'text-gray-900',
    profit: 'text-green-600',
    warning: 'text-amber-600',
    danger: 'text-red-600',
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={clsx('text-2xl font-bold', variantStyles[variant])}>{value}</div>
    </div>
  );
}

function RiskBadge({ rate }: { rate: number }) {
  let label: string;
  let className: string;

  if (rate >= 60) {
    label = '低风险';
    className = 'bg-green-100 text-green-800';
  } else if (rate >= 40) {
    label = '中风险';
    className = 'bg-amber-100 text-amber-800';
  } else {
    label = '高风险';
    className = 'bg-red-100 text-red-800';
  }

  return (
    <span
      className={clsx(
        'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
        className
      )}
    >
      {label}
    </span>
  );
}
